<script lang="ts">
  import {
    barHeight,
    modelDevelops,
    nextHourBars,
    nextHourSentence,
  } from '../../forecast/nexthour'
  import { radarLine } from '../../forecast/now'
  import { radar, radarHere } from '../../state/now.svelte'
  import { shownName } from '../../state/shown'
  import Icon from '../Icon.svelte'

  /**
   * The next hour, above the phone's nav on every section but Map (Phase 11
   * plan §5.2): the thirty bars in miniature, the sentence, the frame and the
   * place. It reads the radar through the same functions as `NextHour`, so the
   * strip and the section cannot say two things. A tap opens Forecast.
   *
   * With no answer to give — a point off the radars, the nowcast off, a frame
   * too old — it is not drawn at all, never a strip of dashes.
   */

  let { timezone, onOpen }: { timezone: string | null; onOpen: () => void } = $props()

  let reading = $derived(radarHere())
  let bars = $derived(nextHourBars(reading, radar.clock))
  let develops = $derived(modelDevelops(reading?.model, radar.clock))
  let sentence = $derived(bars ? nextHourSentence(bars, radar.clock, true, develops) : '')
  let line = $derived.by(() => {
    if (!reading) return ''
    const name = shownName()
    const frame = radarLine({ ...reading, rate_in_h: null }, timezone ?? undefined)
    return name ? `${frame} · ${name}` : frame
  })
</script>

{#if bars}
  <button type="button" class="strip touch-target" aria-label="{sentence}. Open the forecast" onclick={onOpen}>
    <span class="mini" aria-hidden="true">
      {#each bars as bar (bar.at)}
        <i
          class:snow={bar.type === 'snow'}
          class:dry={!bar.precipitating}
          class:unknown={!bar.coverage}
          style:height={bar.precipitating ? `${Math.round(barHeight(bar) * 24)}px` : null}
        ></i>
      {/each}
    </span>
    <span class="text">
      <b>{sentence}</b>
      <span class="line">{line}</span>
    </span>
    <Icon name="chevron-right" size={20} strokeWidth={2.5} />
  </button>
{/if}

<style>
  /* 54 px over a 3 px rule, the page colour, square (the mockup's `.peek`). */
  .strip {
    flex: none;
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    height: 54px;
    padding: 0 12px 0 16px;
    border: 0;
    border-top: 3px solid var(--color-rule);
    background: var(--color-bg);
    color: var(--color-text);
    text-align: left;
    cursor: pointer;
  }

  .mini {
    flex: none;
    display: flex;
    align-items: flex-end;
    gap: 1px;
    height: 24px;
  }

  .mini i {
    width: 2px;
    background: var(--color-rain);
  }

  .mini i.snow {
    background: var(--color-snow);
  }

  /* A dry bar is a stub on the base line, so the strip reads as a scale. */
  .mini i.dry {
    height: 3px;
    background: var(--color-rule);
  }

  .mini i.unknown {
    height: 1px;
  }

  .text {
    flex: 1 1 auto;
    min-width: 0;
    display: grid;
  }

  b,
  .line {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  b {
    font-size: 15px;
    font-weight: 800;
  }

  .line {
    font-size: 12px;
    color: var(--color-muted);
  }
</style>
